import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import BrandLogo from './BrandLogo';
import ColorModeToggle from './ColorModeToggle';
import { getNavEnter, getOverlayFade, mobLink, mobStagger, navItem, staggerContainer } from '../motion/variants';

/** type: route = dedicated page, hash = home page section */
const LINKS = [
  { to: '/products', label: 'Products', type: 'route' },
  { to: '/#innovation', label: 'Innovation', type: 'hash' },
  { to: '/sustainability', label: 'Sustainability', type: 'route' },
  { to: '/#markets', label: 'Markets', type: 'hash' },
  { to: '/innovation', label: 'Case Studies', type: 'route' },
];

function NavAnchor({ link, className, onNavigate }) {
  return (
    <Link to={link.to} className={className} onClick={onNavigate}>
      {link.label}
    </Link>
  );
}

export default function Navigation() {
  const [mobOpen, setMobOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navEnter = getNavEnter();
  const overlayFade = getOverlayFade();

  useEffect(() => {
    let ticking = false;
    const update = () => {
      setScrolled(window.scrollY > 48);
      ticking = false;
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeMob = () => setMobOpen(false);

  useEffect(() => {
    document.body.style.overflow = mobOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobOpen]);

  return (
    <>
      <motion.nav
        id="nav"
        className={scrolled ? 'scrolled' : ''}
        initial={navEnter.initial}
        animate={navEnter.animate}
        transition={navEnter.transition}
      >
        <div className="nav-inner">
          <Link to="/#hero" className="logo-a logo-a--nav" onClick={closeMob}>
            <span className="logo-frame logo-frame--nav">
              <BrandLogo placement="nav" />
            </span>
          </Link>

          <div className="nav-cluster">
            <motion.ul className="nav-ul" variants={staggerContainer} initial="hidden" animate="visible">
              {LINKS.map((link) => (
                <motion.li key={link.to} variants={navItem}>
                  <NavAnchor link={link} onNavigate={closeMob} />
                </motion.li>
              ))}
            </motion.ul>

            <div className="nav-actions">
              <ColorModeToggle className="nav-color-toggle" />
              <Link to="/contact" className="nav-cta" onClick={closeMob}>
                Contact
              </Link>
              <button type="button" className="burger" aria-label="Open menu" onClick={() => setMobOpen(true)}>
                <span />
                <span />
                <span />
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {mobOpen && (
          <motion.div
            id="mob"
            className="open"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
            initial={overlayFade.initial}
            animate={overlayFade.animate}
            exit={overlayFade.exit}
            transition={overlayFade.transition}
          >
            <button type="button" className="mob-close" aria-label="Close menu" onClick={closeMob}>
              ×
            </button>
            <span className="logo-frame logo-frame--mob">
              <BrandLogo placement="nav" />
            </span>
            <motion.div className="mob-links" variants={mobStagger} initial="hidden" animate="visible">
              <div className="mob-links__theme">
                <ColorModeToggle />
              </div>
              {LINKS.map((link) => (
                <motion.div key={link.to} variants={mobLink}>
                  <NavAnchor link={link} onNavigate={closeMob} />
                </motion.div>
              ))}
              <motion.div variants={mobLink}>
                <Link to="/contact" className="mob-cta" onClick={closeMob}>
                  Contact
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
